import isNumber from 'lodash/isNumber';

import {
  type BoxDataSet,
  type BreakdownData,
  type DataProviderSet,
  DataType,
  isBoxBreakdownDataSet,
  type SingleDataPoint,
} from '../../DataProvider/types';
import {
  type PlotFilter,
  PlotFilterType,
} from '../../PageConfigProvider/types/item';

const groupData = <T extends SingleDataPoint | BoxDataSet>(
  data: BreakdownData<T>[]
): BreakdownData<T>[] => {
  const groupedData = data.reduce<Record<string, T[]>>((acc, entity) => {
    const secondaryDim = entity.dim;
    const id = entity.id;
    entity.breakdown.forEach((val) => {
      const dim = val.dim;
      if (!acc[dim]) acc[dim] = [];
      acc[dim].push({ ...val, dim: secondaryDim, id });
    });

    return acc;
  }, {});

  return Object.entries(groupedData).map(([dim, values]) => ({
    dim,
    breakdown: values,
  }));
};

// "Other" segment has -1 id
export const isOtherSegment = (segment: {
  dim: string;
  id?: string | null;
}): boolean => {
  if (segment.id === '-1') return true;
  const dim = segment.dim?.trim().toLowerCase() ?? '';
  return dim === '' || dim === 'other';
};

export const groupAndFilterData = ({
  data,
  groupBySegment,
  plotFilters,
  hideOther,
}: {
  data: DataProviderSet;
  groupBySegment?: boolean;
  plotFilters?: PlotFilter[];
  hideOther?: boolean;
}): DataProviderSet => {
  const otherFilteredData: DataProviderSet = (() => {
    if (!hideOther || !data) return data;

    if (data.type === DataType.SINGLE) {
      return {
        type: DataType.SINGLE,
        data: data.data.filter((d) => !isOtherSegment(d)),
      };
    }

    if (data.type === DataType.BREAKDOWN_SINGLE) {
      const filtered = data.data
        .filter((entity) => !isOtherSegment(entity))
        .map((entity) => ({
          ...entity,
          breakdown: entity.breakdown.filter((b) => !isOtherSegment(b)),
        }));
      // Spread to keep a tree map's `levels` (or a breakdown's `level`).
      return { ...data, data: filtered };
    }

    if (data.type === DataType.BOX && isBoxBreakdownDataSet(data)) {
      const filtered = data.data
        .filter((entity) => !isOtherSegment(entity))
        .map((entity) => ({
          ...entity,
          breakdown: entity.breakdown.filter((b) => !isOtherSegment(b)),
        }));
      return { type: DataType.BOX, data: filtered };
    }

    return data;
  })();

  const groupedData: DataProviderSet = (() => {
    if (!groupBySegment || !otherFilteredData) return otherFilteredData;

    if (otherFilteredData.type === DataType.BREAKDOWN_SINGLE) {
      const groupedData = groupData(otherFilteredData.data);
      return { type: DataType.BREAKDOWN_SINGLE, data: groupedData };
    }

    if (
      otherFilteredData.type === DataType.BOX &&
      isBoxBreakdownDataSet(otherFilteredData)
    ) {
      const groupedData = groupData(otherFilteredData.data);
      return { type: DataType.BOX, data: groupedData };
    }

    return otherFilteredData;
  })();

  const filteredData: DataProviderSet = (() => {
    if (!plotFilters || !groupedData) return groupedData;

    const topNValue = plotFilters.find(
      (f) => f.type === PlotFilterType.TOP_N
    )?.value;
    const bottomNValue = plotFilters.find(
      (f) => f.type === PlotFilterType.BOTTOM_N
    )?.value;

    if (!isNumber(topNValue) && !isNumber(bottomNValue)) return groupedData;

    if (groupedData.type === DataType.SINGLE) {
      const sortedData = (groupedData.data as SingleDataPoint[]).sort(
        (a: SingleDataPoint, b: SingleDataPoint) =>
          (b.value ?? 0) - (a.value ?? 0)
      );

      const filteredSingleData = sortedData.filter(
        (d: SingleDataPoint, i: number) =>
          (topNValue && i < topNValue) ||
          (bottomNValue && i >= sortedData.length - bottomNValue)
      );

      return {
        type: DataType.SINGLE,
        data: filteredSingleData,
      };
    }

    if (
      groupedData.type === DataType.BREAKDOWN_SINGLE &&
      'data' in groupedData
    ) {
      const breakdownEntities = groupedData.data;
      const maxBreakdownLength = breakdownEntities.reduce(
        (max, entity) => Math.max(max, entity.breakdown.length),
        0
      );

      const topNOnlyFilter = plotFilters.find(
        (f) => f.type === PlotFilterType.TOP_N
      );
      const topNOnlyValue = topNOnlyFilter?.value;

      const hasBottomFilter = plotFilters.some(
        (f) => f.type === PlotFilterType.BOTTOM_N
      );

      const shouldSliceTopEntities =
        isNumber(topNOnlyValue) &&
        topNOnlyValue > 0 &&
        !hasBottomFilter &&
        maxBreakdownLength <= 1;

      if (shouldSliceTopEntities) {
        const sortedEntities = [...breakdownEntities].sort(
          (a, b) => (b.breakdown[0]?.value ?? 0) - (a.breakdown[0]?.value ?? 0)
        );
        return { ...groupedData, data: sortedEntities.slice(0, topNOnlyValue) };
      }

      const filteredGroupedData = breakdownEntities.map(
        (entity: BreakdownData<SingleDataPoint>) => {
          const filteredBreakdown: SingleDataPoint[] = [];

          const sortedBreakdown = entity.breakdown.sort(
            (a: SingleDataPoint, b: SingleDataPoint) =>
              (b.value ?? 0) - (a.value ?? 0)
          );
          if (topNValue) {
            filteredBreakdown.push(...sortedBreakdown.slice(0, topNValue));
          }
          if (bottomNValue) {
            filteredBreakdown.push(
              ...sortedBreakdown.slice(sortedBreakdown.length - bottomNValue)
            );
          }

          return {
            dim: entity.dim,
            breakdown: filteredBreakdown,
          };
        }
      );
      return { ...groupedData, data: filteredGroupedData };
    }

    if (
      groupedData.type === DataType.BOX &&
      isBoxBreakdownDataSet(groupedData)
    ) {
      const sortedGroupedData = groupedData?.data?.sort(
        (a, b) =>
          b.breakdown.reduce((acc, val) => acc + val.box.q50, 0) -
          a.breakdown.reduce((acc, val) => acc + val.box.q50, 0)
      );

      const filteredGroupedData = sortedGroupedData?.filter((_d, i) => {
        return (
          (topNValue && i < topNValue) ||
          (bottomNValue && i >= sortedGroupedData.length - bottomNValue)
        );
      });
      return { type: DataType.BOX, data: filteredGroupedData };
    }

    return groupedData;
  })();

  return filteredData;
};
